import React from 'react';
import { CheckCircle, Phone, Mail, MapPin } from 'lucide-react';
import { parseBoldText } from './resumeHelpers';
import { getIconForType } from '../../../utils/socialLinks/socialLinkIcons';

// 업데이트 중 표시 컴포넌트
export const UpdatingIndicator = ({ text }) => (
  <div className="flex items-center gap-2 text-green-700 text-sm mt-2 py-2 px-3 bg-green-100 rounded-md">
    <CheckCircle className="w-4 h-4" />
    <span>{text}</span>
  </div>
);

// 섹션 제목 컴포넌트 (Template1 스타일)
export const SectionTitle = ({ icon: IconComponent, title }) => (
  <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-300 uppercase tracking-wider flex items-center">
    {IconComponent && <IconComponent className="w-5 h-5 text-gray-700 mr-2" />}
    {title}
  </h2>
);

// 섹션 제목 컴포넌트 (Template5 스타일)
export const SectionTitleWithLine = ({ title }) => (
  <>
    <h2 className="text-base font-bold text-gray-700 tracking-[0.2em] mb-2 uppercase">
      {title}
    </h2>
    <div className="w-full h-px bg-gray-300 mb-4"></div>
  </>
);

// 불릿 포인트 렌더링 컴포넌트 (Template1에서 사용)
export const renderBulletPoints = (text, bullet = "•", liClassName = "", textClassName = "", isPlaceholder) => {
  if (!text || typeof text !== 'string') return null;
  const effectiveTextClassName = isPlaceholder ? 'text-gray-300 italic' : (textClassName || 'text-gray-700');
  const bulletColorClass = isPlaceholder ? 'text-gray-300' : (textClassName || 'text-gray-700');

  return (
    <ul className="list-none space-y-1.5">
      {text.split('\n').map((line, i) => {
        let cleanLine = line.trim();
        if (cleanLine.startsWith('- ') || cleanLine.startsWith('• ')) {
          cleanLine = cleanLine.substring(2);
        } else if (cleanLine.startsWith('-') || cleanLine.startsWith('•')) {
          cleanLine = cleanLine.substring(1);
        }
        
        return cleanLine.trim() && (
          <li key={i} className={`relative pl-5 ${liClassName}`}>
            <span className={`absolute left-0 font-semibold ${bulletColorClass}`}>{bullet}</span>
            <span className={effectiveTextClassName}>{parseBoldText(cleanLine.trim())}</span>
          </li>
        );
      })}
    </ul>
  );
};

// Contact Info 렌더링 컴포넌트
export const ContactInfo = ({ 
  phone, 
  email, 
  address, 
  socialLinks = [], 
  hasRealData = {},
  isFieldUpdating = () => false,
  updatingField,
  className = "",
  iconSize = "w-4 h-4",
  showLabels = false,
  direction = "horizontal", // horizontal or vertical
  align = "left", // left, right, center
  useEmoji = false, // 이모지 사용 여부
  textColor = "text-gray-600" // 기본 텍스트 색상
}) => {
  const items = [];
  
  // Phone
  if (phone) {
    items.push({
      icon: Phone,
      emoji: '📞',
      value: phone,
      field: 'phone',
      isReal: hasRealData.phone,
      isLink: false
    });
  }
  
  // Email
  if (email) {
    items.push({
      icon: Mail,
      emoji: '✉',
      value: email,
      field: 'email',
      isReal: hasRealData.email,
      isLink: false
    });
  }
  
  // Address (using location type)
  if (address) {
    items.push({
      icon: MapPin,
      emoji: '📍',
      value: address,
      field: 'address',
      isReal: hasRealData.address,
      isLink: false
    });
  }
  
  const containerClass = direction === 'horizontal' 
    ? 'grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2' 
    : 'space-y-1';
    
  const alignClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : '';
  const textAlignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : '';
  
  return (
    <div className={`${containerClass} ${textAlignClass} text-sm ${className}`}>
      {/* Basic contact info */}
      {items.map((item, idx) => (
        <div key={idx} className={`flex items-center gap-2 ${alignClass} ${isFieldUpdating(item.field, updatingField) ? 'text-green-600' : (!item.isReal ? 'text-gray-300' : textColor)}`}>
          {useEmoji ? (
            <span className="text-gray-400">{item.emoji}</span>
          ) : (
            <item.icon className={`${iconSize} flex-shrink-0 ${!item.isReal ? 'text-gray-200' : ''}`} />
          )}
          <span className={!item.isReal ? 'italic' : ''}>
            {item.value}
          </span>
        </div>
      ))}
      
      {/* Social Links */}
      {socialLinks && socialLinks.map((social, index) => {
        const platform = social.type || social.platform || '';
        const url = social.url || '';
        let displayText = url.replace(/^https?:\/\/(www\.)?/, '');
        
        return (
          <div key={`social-${index}`} className={`flex items-center gap-2 ${alignClass} ${isFieldUpdating('social_links', updatingField) ? 'text-green-600' : textColor}`}>
            {useEmoji ? (
              <span className="text-gray-400">🌐</span>
            ) : (
              <span className={`${iconSize} flex-shrink-0`}>
                {getIconForType(social.icon || social.type)}
              </span>
            )}
            {platform === 'location' ? (
              <span>{url}</span>
            ) : (
              <a href={url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 underline">
                {displayText}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Placeholder 스타일 유틸리티
export const getPlaceholderClass = (hasRealData, defaultClass = "text-gray-700", placeholderClass = "text-gray-300 italic") => {
  return hasRealData ? defaultClass : placeholderClass;
};

// 날짜 범위 표시 컴포넌트
export const DateRange = ({ startDate, endDate, hasRealData, className = "" }) => (
  <span className={getPlaceholderClass(hasRealData, className)}>
    {startDate} - {endDate}
  </span>
);

// Inline Contact Info 렌더링 컴포넌트 (Template2 스타일)
export const ContactInfoInline = ({ 
  phone, 
  email, 
  address, 
  socialLinks = [], 
  hasRealData = {},
  className = "",
  separator = " | ",
  showIcons = false,
  textColor = "text-gray-600"
}) => {
  const items = [];
  
  if (email) {
    items.push({
      icon: Mail,
      value: email,
      isReal: hasRealData.email
    });
  }
  
  if (phone) {
    items.push({
      icon: Phone,
      value: phone,
      isReal: hasRealData.phone
    });
  }
  
  if (address) {
    items.push({
      icon: MapPin,
      value: address,
      isReal: hasRealData.address
    });
  }
  
  // Add social links
  if (socialLinks) {
    socialLinks.forEach(social => {
      const url = social.url || '';
      let displayText = url.replace(/^https?:\/\/(www\.)?/, '');
      items.push({
        icon: social.type,
        value: displayText,
        url: url,
        type: social.type,
        isReal: true
      });
    });
  }
  
  return (
    <div className={`flex flex-wrap gap-4 text-sm ${className}`}>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span className={textColor}>{separator.trim()}</span>}
          <span className={!item.isReal ? 'text-gray-300 italic' : textColor}>
            {showIcons && item.icon && (
              <span className="inline-block mr-1">
                {typeof item.icon === 'string' ? getIconForType(item.icon) : <item.icon className="w-4 h-4 inline" />}
              </span>
            )}
            {item.url ? (
              <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 underline">
                {item.value}
              </a>
            ) : (
              item.value
            )}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

// 경력사항 섹션 컴포넌트
export const ExperienceSection = ({ 
  experiences, 
  hasRealData, 
  className = "", 
  itemClassName = "",
  datePosition = "right", // right, left, inline
  showProjects = true,
  projectIndent = "pl-4",
  bulletStyle = "•",
  dateClassName = "",
  companyClassName = "",
  positionClassName = "",
  descriptionClassName = "",
  projectTitleClassName = "",
  projectDescriptionClassName = ""
}) => {
  return (
    <div className={className}>
      {experiences.map((exp, index) => (
        <div key={index} className={`${itemClassName} ${index < experiences.length - 1 ? 'mb-6' : ''}`}>
          <div className={datePosition === "right" ? "flex justify-between items-start" : ""}>
            <div className={datePosition === "right" ? "flex-1" : ""}>
              {datePosition === "left" && (
                <div className={getPlaceholderClass(hasRealData.experiences, dateClassName)}>
                  {exp.startDate} - {exp.endDate}
                </div>
              )}
              <h3 className={getPlaceholderClass(hasRealData.experiences, positionClassName)}>
                {exp.position}
              </h3>
              <p className={getPlaceholderClass(hasRealData.experiences, companyClassName)}>
                {exp.company}
              </p>
              {datePosition === "inline" && (
                <span className={getPlaceholderClass(hasRealData.experiences, dateClassName)}>
                  {exp.startDate} - {exp.endDate}
                </span>
              )}
            </div>
            {datePosition === "right" && (
              <div className={getPlaceholderClass(hasRealData.experiences, dateClassName)}>
                {exp.startDate} - {exp.endDate}
              </div>
            )}
          </div>
          
          {exp.overallDescription && (
            <div className={`mt-2 ${descriptionClassName}`}>
              {renderBulletPoints(exp.overallDescription, bulletStyle, "", descriptionClassName, !hasRealData.experiences)}
            </div>
          )}
          
          {showProjects && exp.projects && exp.projects.length > 0 && (
            <div className={`mt-3 space-y-3 ${projectIndent}`}>
              {exp.projects.map((project, projIndex) => (
                <div key={projIndex}>
                  <h4 className={getPlaceholderClass(hasRealData.experiences, projectTitleClassName)}>
                    {project.projectName}
                  </h4>
                  {project.projectDescription && (
                    <div className={`mt-1 ${projectDescriptionClassName}`}>
                      {renderBulletPoints(project.projectDescription, "-", "", projectDescriptionClassName, !hasRealData.experiences)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// 학력 섹션 컴포넌트
export const EducationSection = ({ 
  education, 
  hasRealData, 
  className = "", 
  itemClassName = "",
  datePosition = "right",
  showMajor = true,
  showGPA = true,
  dateClassName = "",
  schoolClassName = "",
  degreeClassName = "",
  majorClassName = "",
  gpaClassName = ""
}) => {
  return (
    <div className={className}>
      {education.map((edu, index) => (
        <div key={index} className={`${itemClassName} ${index < education.length - 1 ? 'mb-4' : ''}`}>
          <div className={datePosition === "right" ? "flex justify-between items-start" : ""}>
            <div className={datePosition === "right" ? "flex-1" : ""}>
              {datePosition === "left" && (
                <div className={getPlaceholderClass(hasRealData.education, dateClassName)}>
                  {edu.startDate} - {edu.endDate}
                </div>
              )}
              <h3 className={getPlaceholderClass(hasRealData.education, degreeClassName)}>
                {edu.degree}
              </h3>
              <p className={getPlaceholderClass(hasRealData.education, schoolClassName)}>
                {edu.school}
              </p>
              {showMajor && edu.major && (
                <p className={getPlaceholderClass(hasRealData.education, majorClassName)}>
                  {edu.major}
                </p>
              )}
              {showGPA && edu.gpa && (
                <p className={getPlaceholderClass(hasRealData.education, gpaClassName)}>
                  {edu.gpa}
                </p>
              )}
            </div>
            {datePosition === "right" && (
              <div className={getPlaceholderClass(hasRealData.education, dateClassName)}>
                {edu.startDate} - {edu.endDate}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// 자격증 섹션 컴포넌트
export const CertificateSection = ({ 
  certificates, 
  hasRealData, 
  className = "", 
  itemClassName = "",
  showIssuer = true,
  dateFormat = "separate", // separate, inline
  nameClassName = "",
  issuerClassName = "",
  dateClassName = ""
}) => {
  return (
    <div className={className}>
      {certificates.map((cert, index) => (
        <div key={index} className={`${itemClassName} ${index < certificates.length - 1 ? 'mb-3' : ''}`}>
          <h3 className={getPlaceholderClass(hasRealData.certificates, nameClassName)}>
            {cert.name}
          </h3>
          {dateFormat === "inline" ? (
            <p className={getPlaceholderClass(hasRealData.certificates, issuerClassName)}>
              {showIssuer && cert.issuer} • {cert.date}
            </p>
          ) : (
            <>
              {showIssuer && (
                <p className={getPlaceholderClass(hasRealData.certificates, issuerClassName)}>
                  {cert.issuer}
                </p>
              )}
              <p className={getPlaceholderClass(hasRealData.certificates, dateClassName)}>
                {cert.date}
              </p>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

// 기술 섹션 컴포넌트
export const SkillSection = ({ 
  skills, 
  hasRealData, 
  variant = "tags", // tags, list, inline
  className = "", 
  itemClassName = "",
  tagClassName = "",
  bulletStyle = "•",
  bulletClassName = "",
  textClassName = ""
}) => {
  if (variant === "tags") {
    return (
      <div className={`flex flex-wrap gap-2 ${className}`}>
        {skills.map((skill, index) => (
          <span 
            key={index} 
            className={getPlaceholderClass(hasRealData.skills, tagClassName || "px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm")}
          >
            {skill}
          </span>
        ))}
      </div>
    );
  } else if (variant === "list") {
    return (
      <ul className={`space-y-1 ${className}`}>
        {skills.map((skill, index) => (
          <li key={index} className={`flex items-start gap-2 ${itemClassName}`}>
            <span className={getPlaceholderClass(hasRealData.skills, bulletClassName || "text-gray-400")}>{bulletStyle}</span>
            <span className={getPlaceholderClass(hasRealData.skills, textClassName || "text-gray-600")}>{skill}</span>
          </li>
        ))}
      </ul>
    );
  } else { // inline
    return (
      <p className={getPlaceholderClass(hasRealData.skills, className)}>
        {skills.join(" • ")}
      </p>
    );
  }
};

// 논문 섹션 컴포넌트
export const PaperSection = ({ 
  papers, 
  hasRealData, 
  getPaperTypeText,
  className = "", 
  itemClassName = "",
  titleClassName = "",
  authorsClassName = "",
  journalClassName = "",
  typeClassName = "",
  showType = true
}) => {
  return (
    <div className={className}>
      {papers.map((paper, index) => (
        <div key={index} className={`${itemClassName} ${index < papers.length - 1 ? 'mb-4' : ''}`}>
          <h3 className={getPlaceholderClass(hasRealData.papers, titleClassName)}>
            {paper.title}
          </h3>
          <p className={getPlaceholderClass(hasRealData.papers, authorsClassName)}>
            {paper.authors}
          </p>
          <p className={getPlaceholderClass(hasRealData.papers, journalClassName)}>
            {paper.journal}, {paper.year}
            {showType && paper.type && (
              <span className={`ml-2 ${typeClassName}`}>
                {getPaperTypeText(paper.type)}
              </span>
            )}
          </p>
        </div>
      ))}
    </div>
  );
};

// 핵심 역량 섹션 컴포넌트
export const CoreCompetenciesSection = ({ 
  keyStrengths, 
  hasRealData, 
  className = "", 
  itemClassName = "",
  bulletStyle = "•",
  bulletClassName = "",
  textClassName = ""
}) => {
  return (
    <ul className={`space-y-2 ${className}`}>
      {keyStrengths.map((strength, index) => (
        <li key={index} className={`flex items-start gap-2 ${itemClassName}`}>
          <span className={getPlaceholderClass(hasRealData.keyStrengths, bulletClassName || "text-gray-400 mt-1")}>{bulletStyle}</span>
          <span className={getPlaceholderClass(hasRealData.keyStrengths, textClassName || "text-gray-600")}>{strength}</span>
        </li>
      ))}
    </ul>
  );
};

// 섹션 래퍼 컴포넌트
export const ResumeSection = ({ 
  title, 
  icon: IconComponent, 
  children, 
  isUpdating = false,
  variant = "default", // default, bordered, underlined, uppercase
  titleClassName = "",
  sectionClassName = "mb-6",
  showIcon = true
}) => {
  const titleStyles = {
    default: "text-lg font-bold text-gray-800 mb-4",
    bordered: "text-lg font-bold text-gray-800 mb-4 pb-2 border-b border-gray-300",
    underlined: "text-base font-bold text-gray-700 tracking-[0.2em] mb-2 uppercase",
    uppercase: "text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-300 uppercase tracking-wider"
  };

  return (
    <section className={sectionClassName}>
      {variant === "underlined" ? (
        <>
          <h2 className={`${titleStyles[variant]} ${titleClassName}`}>
            {showIcon && IconComponent && <IconComponent className="w-5 h-5 text-gray-700 mr-2 inline" />}
            {title}
          </h2>
          <div className="w-full h-px bg-gray-300 mb-4"></div>
        </>
      ) : (
        <h2 className={`${titleStyles[variant]} ${titleClassName} ${isUpdating ? 'text-green-600' : ''}`}>
          {showIcon && IconComponent && <IconComponent className="w-5 h-5 text-gray-700 mr-2 inline" />}
          {title}
        </h2>
      )}
      {children}
    </section>
  );
};

// 헤더 컴포넌트
export const ResumeHeader = ({ 
  name, 
  title, 
  phone,
  email,
  address,
  socialLinks,
  hasRealData,
  variant = "default", // default, centered, sidebar, split, minimal
  backgroundColor = "",
  nameClassName = "",
  titleClassName = "",
  contactClassName = "",
  containerClassName = ""
}) => {
  const layouts = {
    default: (
      <div className={`${backgroundColor || 'bg-gray-50'} p-8 ${containerClassName}`}>
        <h1 className={getPlaceholderClass(hasRealData.name, nameClassName || "text-3xl font-bold text-gray-800 mb-2")}>
          {name}
        </h1>
        <p className={getPlaceholderClass(hasRealData.title, titleClassName || "text-xl text-gray-600 mb-4")}>
          {title}
        </p>
        <ContactInfo
          phone={phone}
          email={email}
          address={address}
          socialLinks={socialLinks}
          hasRealData={hasRealData}
          className={contactClassName}
        />
      </div>
    ),
    
    centered: (
      <div className={`${backgroundColor || 'bg-white'} p-8 text-center ${containerClassName}`}>
        <h1 className={getPlaceholderClass(hasRealData.name, nameClassName || "text-4xl font-bold text-gray-800 mb-2")}>
          {name}
        </h1>
        <p className={getPlaceholderClass(hasRealData.title, titleClassName || "text-xl text-gray-600 mb-4")}>
          {title}
        </p>
        <ContactInfo
          phone={phone}
          email={email}
          address={address}
          socialLinks={socialLinks}
          hasRealData={hasRealData}
          className={contactClassName}
          align="center"
        />
      </div>
    ),
    
    split: (
      <div className={`flex ${backgroundColor || 'bg-white'} ${containerClassName}`}>
        <div className="w-2/3 p-8">
          <h1 className={getPlaceholderClass(hasRealData.name, nameClassName || "text-5xl font-light text-gray-800 mb-4")}>
            {name}
          </h1>
          <p className={getPlaceholderClass(hasRealData.title, titleClassName || "text-sm text-gray-600 uppercase tracking-wider")}>
            {title}
          </p>
        </div>
        <div className="w-px bg-gray-300"></div>
        <div className="w-1/3 p-8">
          <ContactInfo
            phone={phone}
            email={email}
            address={address}
            socialLinks={socialLinks}
            hasRealData={hasRealData}
            className={contactClassName}
            direction="vertical"
          />
        </div>
      </div>
    ),
    
    minimal: (
      <div className={`${backgroundColor || 'bg-white'} p-6 ${containerClassName}`}>
        <div className="flex justify-between items-start">
          <div>
            <h1 className={getPlaceholderClass(hasRealData.name, nameClassName || "text-2xl font-bold text-gray-800")}>
              {name}
            </h1>
            <p className={getPlaceholderClass(hasRealData.title, titleClassName || "text-sm text-gray-600")}>
              {title}
            </p>
          </div>
          <ContactInfoInline
            phone={phone}
            email={email}
            socialLinks={socialLinks}
            hasRealData={hasRealData}
            className={contactClassName}
          />
        </div>
      </div>
    )
  };

  return layouts[variant] || layouts.default;
};

// 요약/소개 섹션 컴포넌트
export const SummarySection = ({
  summary,
  hasRealData,
  className = "",
  textClassName = ""
}) => {
  if (!summary) return null;
  
  return (
    <p className={getPlaceholderClass(hasRealData.summary, textClassName || `text-sm leading-relaxed ${className}`)}>
      {summary}
    </p>
  );
};
